import {
  Box,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';

import type { Transaction } from 'types/api/transaction';
import type { CsvExportParams } from 'types/client/address';

import type { ResourceName } from 'lib/api/resources';
import CsvExportForm from 'ui/csvExport/CsvExportForm';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  hash?: string;
  resource: ResourceName;
  filterType?: CsvExportParams['filterType'] | null;
  filterValue?: CsvExportParams['filterValue'] | null;
  typeFilter?: string;
  tokenAddressHashFilter?: string;
  fileNameTemplate: string;
  exportType: CsvExportParams['type'] | undefined;
  items?: Array<Transaction>;
}

const CsvExportRangeWarning = ({ message }: { message: string }) => {
  return (
    <Flex
      data-testid="csv-export-range-warning"
      position="fixed"
      top={{ base: '44px', md: '44px' }}
      left="50%"
      transform="translateX(-50%)"
      zIndex="toast"
      w={{ base: 'calc(100vw - 32px)', md: '360px' }}
      maxW="360px"
      alignItems="flex-start"
      gap="16px"
      p="20px"
      bg="#1E1E1E"
      borderRadius="8px"
      boxShadow="0px 4px 10px rgba(0, 0, 0, 0.10)"
    >
      <IconButton
        aria-label="Warning icon"
        data-testid="csv-export-range-warning-icon"
        icon={ (
          <Box as="span" color="#1E1E1E" fontSize="18px" fontWeight={ 700 } lineHeight="1">
            !
          </Box>
        ) }
        flexShrink={ 0 }
        w="24px"
        h="24px"
        minW="24px"
        borderRadius="full"
        bg="#FF7D00"
        color="#1E1E1E"
        _hover={{ bg: '#FF7D00' }}
        _active={{ bg: '#FF7D00' }}
      />
      <Flex direction="column" flex={ 1 } gap="4px" alignItems="flex-start">
        <Text color="white" fontSize="16px" fontWeight={ 500 } lineHeight="24px">
          Warning
        </Text>
        <Text color="white" fontSize="14px" fontWeight={ 400 } lineHeight="22px">
          { message }
        </Text>
      </Flex>
    </Flex>
  );
};

const CsvExportDialog = ({
  isOpen,
  onClose,
  title = 'Export Transactions',
  hash,
  resource,
  filterType,
  filterValue,
  typeFilter,
  tokenAddressHashFilter,
  fileNameTemplate,
  exportType,
  items,
}: Props) => {
  const [ validationError, setValidationError ] = React.useState<string>();

  const modalBg = useColorModeValue('#FFFFFF', '#171717');
  const modalBorderColor = useColorModeValue('rgba(0,0,0,0.1)', 'rgba(20, 220, 172, 0.3)');
  const titleColor = useColorModeValue('#000000', 'white');
  const closeButtonColor = useColorModeValue('#616161', '#A5A5A5');
  const closeButtonHoverColor = useColorModeValue('#000000', 'white');

  React.useEffect(() => {
    if (!isOpen) {
      setValidationError(undefined);
    }
  }, [ isOpen ]);

  return (
    <Modal
      isOpen={ isOpen }
      onClose={ onClose }
      isCentered
      motionPreset="none"
      size="unstyled"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(2px)"/>
      { validationError && <CsvExportRangeWarning message={ validationError }/> }
      <ModalContent
        maxW={{ base: 'calc(100vw - 32px)', md: '560px' }}
        bg={ modalBg }
        border="0.6px solid"
        borderColor={ modalBorderColor }
        borderRadius="8px"
        boxShadow="none"
        color={ titleColor }
        mx={{ base: 4, md: 0 }}
        px="32px"
        pt="34px"
        pb="0"
      >
        <ModalHeader
          as="h2"
          p={ 0 }
          mb="10px"
          fontSize="24px"
          fontWeight={ 500 }
          lineHeight="36px"
          color={ titleColor }
        >
          { title }
        </ModalHeader>
        <ModalCloseButton
          top="24px"
          right="24px"
          color={ closeButtonColor }
          _hover={{ color: closeButtonHoverColor }}
        />
        <ModalBody p={ 0 }>
          <CsvExportForm
            hash={ hash }
            resource={ resource }
            filterType={ filterType }
            filterValue={ filterValue }
            typeFilter={ typeFilter }
            tokenAddressHashFilter={ tokenAddressHashFilter }
            fileNameTemplate={ fileNameTemplate }
            exportType={ exportType }
            submitLabel="Export"
            onComplete={ onClose }
            onValidationErrorChange={ setValidationError }
            items={ items }
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(CsvExportDialog);
